/* eslint-disable no-inner-declarations */

import { SystemFuncArgs } from '$legacyecstatic';
import { environmentVars } from 'constants/services';
import { Components } from 'editor/components';
import Cursor from 'editor/components/Cursor';
import DomElement from 'editor/components/DomElement';
import Events from 'editor/components/Events';
import MapboxCamera from 'editor/components/map/MapboxCamera';
import MapboxCanvasSource from 'editor/components/map/MapboxCanvasSource';
import MapboxEvents from 'editor/components/map/MapboxEvents';
import MapboxLayer from 'editor/components/map/MapboxLayer';
import MapboxMap from 'editor/components/map/MapboxMap';
import MapboxRasterSource from 'editor/components/map/MapboxRasterSource';
import mapboxgl from 'mapbox-gl';

const BBHQ = { longitude: -118.149339, latitude: 34.138143 };

const defaultViewport = {
  ...BBHQ,
  zoom: 17,
  pitch: 0,
  bearing: 0,
};

export const manageMapComponents = [MapboxMap];

export function manageMap(args: SystemFuncArgs<Components>): void {
  const { world, components } = args;
  const mapComp = components.get(MapboxMap);

  if (!mapComp.map) {
    const viewport = components.has(MapboxCamera)
      ? components.get(MapboxCamera).tracked.viewport || defaultViewport
      : defaultViewport;

    const map = new mapboxgl.Map({
      ...viewport,
      container: 'map-container', // This should be generic, and set in the MapboxMap component.
      style: mapComp.style,
      accessToken: environmentVars.MAPBOX_API_KEY,
      maxPitch: 0, // Locking pitch right now due to not supporting markups in perspective views.
      maxZoom: 24,
      minZoom: 1.32, // Mapbox for some reason isn't allowing a zoom level lower than ~1.319
      bearingSnap: 0,
      transformRequest: (url) => {
        if (!url) return { url: '' };

        if (url.startsWith('https://apptiles.bluebeam')) {
          return {
            url,
            credentials: 'include',
          };
        }

        return { url };
      },
    });

    mapComp.map = map;

    // Add Map Events
    const mapEvents = new MapboxEvents(world, mapComp);
    const events = new Events(mapEvents);

    world.createEntity().add(events);

    // Create Cursor entity
    world
      .createEntity()
      .add(new DomElement(map.getCanvasContainer()))
      .add(new Cursor())
      .addTag('cursor');
  }

  function changeLanguageOnMap() {
    const supportedLanguages = ['en', 'de'];
    const localStorageLangName = 'BB_LANGUAGE_CODE';
    let mapLanguageCode = 'en';

    const languageCode = localStorage
      .getItem(localStorageLangName)
      ?.split('-')[0];

    if (languageCode && supportedLanguages.includes(languageCode)) {
      mapLanguageCode = languageCode;
    }

    const labels = ['country-label', 'state-label', 'settlement-label'];

    labels.forEach((label) => {
      mapComp.map?.setLayoutProperty(label, 'text-field', [
        'get',
        `name_${mapLanguageCode}`,
      ]);
    });
  }

  // Handle changes in the "style" of the map. Basically this means going between
  // street and satellite modes.
  if (mapComp.styleState.is('updated')) {
    mapComp.styleState.next(); // loading

    if (!mapComp.map) return;

    mapComp.map?.setStyle(mapComp.style);

    // Need to wait for the style to fully load. There is not really great way to
    // do this in Mapbox. So far the best solution is just to wait for the app to
    // complete all its work, and enter an "idle" state, which has an event we can
    // monitor.
    function onIdle() {
      changeLanguageOnMap();

      mapComp.styleState.next(); // loaded
      mapComp.map?.resize();
      // need to re-add sources and layers when switching styles
      // https://stackoverflow.com/questions/36168658/mapbox-gl-setstyle-removes-layers
      world.grabAll(MapboxRasterSource).forEach(({ component }) => {
        component.added = false;
      });

      world.grabAll(MapboxCanvasSource).forEach(({ component }) => {
        component.added = false;
      });

      world.grabAll(MapboxLayer).forEach(({ component }) => {
        component.added = false;
      });

      mapComp.map?.off('idle', onIdle);

      world.systems.run();
    }

    mapComp.map?.on('idle', onIdle);
  }
}
